import React, { useState, useRef } from 'react';
import { AiOutlineCloudUpload, AiOutlineFileText } from 'react-icons/ai';
import { FaCheck } from 'react-icons/fa';
import { LuX, LuExternalLink } from 'react-icons/lu';
import { FiAlertCircle } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import useToastContext from 'hooks/useToastContext';
import { useNavigate } from 'react-router-dom';
import apiPath from 'utils/apiPath';
import { apiPost } from 'utils/apiFetch';

const requiredFields = [
  'title', 'title ar', 'content', 'content ar'
];

const requiredHeaders = [
  'Title', 'Title Ar', 'Content', 'Content Ar'
];



const ImportFAQ = () => {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [validationStatus, setValidationStatus] = useState('idle'); // idle, validating, valid, invalid
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
  const fileInputRef = useRef(null);
  const notification = useToastContext();
  const navigate = useNavigate();

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      parseExcel(selectedFile);
    } else {
      alert('Please select a valid file');
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      parseExcel(droppedFile);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const parseExcel = (file) => {
    setValidationStatus('validating');
    setErrors([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      const binaryData = e.target.result;

      const workbook = XLSX.read(binaryData, {
        type: 'binary',
        codepage: 65001, // UTF-8 support
      });

      // First sheet
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Parse to JSON
      let jsonData = XLSX.utils.sheet_to_json(worksheet, {
        defval: '',
        raw: false,   // IMPORTANT: Convert encoding correctly
      });

      jsonData = jsonData.filter((row) =>
        Object.values(row).some(
          (value) =>
            value !== null &&
            value !== undefined &&
            value.toString().trim() !== '' &&
            !(Array.isArray(value) && value.length === 0) &&
            value !== 0
        )
      );

      // Normalize headers
      const normalizedData = jsonData.map((row) => {
        const newRow = {};
        Object.keys(row).forEach((key) => {
          newRow[key.toLowerCase().trim()] = row[key];
        });
        return newRow;
      });

      validateData(normalizedData);
      console.log(normalizedData);
      setCsvData(normalizedData);
    };

    //Key change: Use readAsBinaryString
    reader.readAsBinaryString(file);
  };

  // const parseExcel = (file) => {

  //   setValidationStatus('validating');
  //   setErrors([]);

  //   const reader = new FileReader();
  //   reader.onload = (e) => {
  //     const data = new Uint8Array(e.target.result);
  //     const workbook = XLSX.read(data, { type: 'array' });

  //     // Take first sheet
  //     const sheetName = workbook.SheetNames[0];
  //     const worksheet = workbook.Sheets[sheetName];

  //     // Parse to JSON
  //     let jsonData = XLSX.utils.sheet_to_json(worksheet, {
  //       defval: '',
  //       raw: true,
  //     });
  //     jsonData = jsonData.filter((row) =>
  //       Object.values(row).some(
  //         (value) =>
  //           value !== null &&
  //           value !== undefined &&
  //           value.toString().trim() !== '' &&
  //           !(Array.isArray(value) && value.length === 0) &&
  //           value !== 0
  //       )
  //     );

  //     // Normalize headers to lowercase
  //     const normalizedData = jsonData.map((row) => {
  //       const newRow = {};
  //       Object.keys(row).forEach((key) => {
  //         newRow[key.toLowerCase().trim()] = row[key];
  //       });
  //       return newRow;
  //     });

  //     validateData(normalizedData);
  //     console.log(normalizedData);
  //     setCsvData(normalizedData);
  //   };

  //   reader.readAsArrayBuffer(file);
  // };

  const validateData = (data) => {
    const validationErrors = [];

    if (data.length === 0) {
      validationErrors.push('File is empty or contains no valid data');
    }

    // Check headers
    const fileHeaders = Object.keys(data[0]).map((h) => h.toLowerCase().trim());
    const missingHeaders = requiredHeaders.filter(
      (header) => !fileHeaders.includes(header.toLowerCase())
    );

    if (missingHeaders.length > 0) {
      validationErrors.push(
        `Missing required headers: ${missingHeaders.join(', ')}`
      );

      setErrors(validationErrors);
      setValidationStatus('invalid');
      return;
    }

    // Validate each row
    data.forEach((row, index) => {
      const rowNumber = index + 2; // considering header at row 1

      // Check required fields
      requiredFields.forEach((field) => {
        const fieldKey = field.toLowerCase();
        if (
          !row[fieldKey] ||
          row[fieldKey].toString().trim() === '' ||
          row[fieldKey] === 0 ||
          (Array.isArray(row[fieldKey]) && row[fieldKey].length === 0)
        ) {
          validationErrors.push(`Row ${rowNumber}: Missing ${field}`);
        }
      });

      // Parse projectSlug if it exists (can be comma-separated)
      // Handle variations: projectslug, project slug, projectSlug
      const projectSlugKey = row.projectslug || row['project slug'] || row.projectSlug;
      if (projectSlugKey) {
        const projectSlugs = projectSlugKey
          .toString()
          .split(',')
          .map((slug) => slug.trim())
          .filter((slug) => slug !== '');
        
        // Store parsed project slugs back to the row for later use
        row._parsedProjectSlugs = projectSlugs;
      }
    });

    setErrors(validationErrors);
    setValidationStatus(validationErrors.length === 0 ? 'valid' : 'invalid');
  };

  const handleUpload = async () => {
    if (validationStatus !== 'valid') return;

    setUploadStatus('uploading');

    const faqs = csvData.map((faq) => {
      const faqData = {
        title: faq.title,
        titleAr: faq['title ar'] || null,
        content: faq.content,
        contentAr: faq['content ar'] || null,
        projectSlug: faq['project slug'],
      };

      // // Include projectSlug(s) if they exist in the file
      // if (faq._parsedProjectSlugs && faq._parsedProjectSlugs.length > 0) {
      //   faqData.projectSlug = faq._parsedProjectSlugs;
      // } else {
      //   // Fallback: parse on the fly if not already parsed
      //   const projectSlugKey = faq.projectslug || faq['project slug'] || faq.projectSlug;
      //   if (projectSlugKey) {
      //     faqData.projectSlug = projectSlugKey
      //       .toString()
      //       .split(',')
      //       .map((slug) => slug.trim())
      //       .filter((slug) => slug !== '');
      //   }
      // }

      return faqData;
    });

    const { data: response } = await apiPost(apiPath.importFAQs, faqs);

    if (response?.success) {
      notification.success(response?.message);
      navigate('/static/faqs');
    } else {
      setUploadStatus('idle');
      notification.error(response?.message);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setCsvData([]);
    setErrors([]);
    setValidationStatus('idle');
    setUploadStatus('idle');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Content Card */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="p-6 space-y-6">
            {/* Step 1: Access the Template */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-gray-900">
                1. Access the FAQ Template:
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>
                  <button
                    onClick={() =>
                      window.open(
                        'https://docs.google.com/spreadsheets/d/13KrY3j9NB4N7GICOee8PJH8Ok6NcyFGb_Av6j6wZWsI/edit?gid=0#gid=0',
                        '_blank'
                      )
                    }
                    className="text-blue-600 hover:text-blue-800 underline inline-flex items-center"
                  >
                    Click on this link to access the Unit Template
                    <LuExternalLink className="w-4 h-4 ml-1" />
                  </button>
                </li>
              </ul>
            </div>

            {/* Step 2: Make a Copy */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-gray-900">
                2. Make a Copy:
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>
                  Once the template opens, go to the "File" menu at the top-left
                  corner.
                </li>
                <li>
                  Select "Make a Copy" from the dropdown menu. This will create
                  a duplicate copy of the original template that you can edit.
                </li>
              </ul>
            </div>

            {/* Step 3: Add Your Changes */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-gray-900">
                3. Add Your Changes:
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Edit the newly created copy of the template as needed.</li>
              </ul>
            </div>

            {/* Step 4: Download as CSV */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-gray-900">
                4. Download as CSV:
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>After making your changes, go back to the "File" menu.</li>
                <li>Select "Download" from the dropdown menu.</li>
                <li>
                  Choose "Comma Separated Values (.csv)" from the options
                  provided. This will convert your template into a CSV file.
                </li>
              </ul>
            </div>

            {/* Step 5: Upload the File */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-gray-900">
                5. Upload the File:
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>
                  Use the upload area provided on the website to upload the CSV
                  file you just downloaded.
                </li>
                <li>Click on the upload button to submit the file.</li>
              </ul>
            </div>

            {/* Upload Area */}
            <div className="rounded-lg p-0">
              {!file ? (
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer bg-gray-50"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <AiOutlineCloudUpload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg text-gray-600 mb-2 font-medium">
                    Click or drag file to this area
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* File Info */}
                  <div className="flex items-center justify-between bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <AiOutlineFileText className="w-8 h-8 text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {(file.size / 1024).toFixed(1)} KB • {csvData.length}{' '}
                          FAQs
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={resetUpload}
                      className="text-gray-500 hover:text-red-600 transition-colors"
                    >
                      <LuX className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Validation Status */}
                  {validationStatus === 'validating' && (
                    <div className="flex items-center justify-center text-blue-600 py-4">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                      Validating data...
                    </div>
                  )}

                  {validationStatus === 'valid' &&
                    uploadStatus !== 'success' && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-center text-green-600 py-2">
                          <FaCheck className="w-5 h-5 mr-2" />
                          All data is valid and ready for upload!
                        </div>
                      </div>
                    )}

                  {validationStatus === 'invalid' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center text-red-600 mb-3">
                        <FiAlertCircle className="w-5 h-5 mr-2" />
                        <span className="font-medium">
                          Validation errors found:
                        </span>
                      </div>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {errors.map((error, index) => (
                          <p key={index} className="text-sm text-red-600">
                            • {error}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Success Message */}
                  {uploadStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                      <FaCheck className="w-12 h-12 text-green-600 mx-auto mb-3" />
                      <h4 className="text-lg font-medium text-green-900 mb-2">
                        Upload Successful!
                      </h4>
                      <p className="text-green-700">
                        {csvData.length} FAQs have been added successfully.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Upload Button */}
              {file &&
                validationStatus === 'valid' &&
                uploadStatus !== 'success' && (
                  <div className="mt-6 text-center">
                    <button
                      onClick={handleUpload}
                      disabled={uploadStatus === 'uploading'}
                      className="bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {uploadStatus === 'uploading' ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                )}
            </div>
          </div>

          {/* Red separator line */}
          <div className="h-1 bg-red-500"></div>
        </div>

        {/* Data Preview */}
        {csvData.length > 0 && validationStatus === 'valid' && (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden mt-6">
            <div className="p-6 border-b bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900">
                Data Preview
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Showing first 5 FAQs
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      No.
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title (Arabic)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Content
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Content (Arabic)
                    </th> 
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Project Slug(s)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {csvData.slice(0, 5).map((faq, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="text-gray-900">
                          {index + 1}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-900">
                          {faq.title}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-900">
                          {faq['title ar']}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-900">
                          {faq.content}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-900">
                          {faq['content ar']}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-900">
                          {faq._parsedProjectSlugs && faq._parsedProjectSlugs.length > 0 ? faq._parsedProjectSlugs.join(', ') : 'No project slug'}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {csvData.length > 5 && (
              <div className="px-6 py-3 bg-gray-50 text-sm text-gray-600 text-center">
                And {csvData.length - 5} more FAQs...
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportFAQ;
